<script lang="ts" setup>
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from 'vue'
import { throttle } from 'lodash'

import { addEventListeners, removeEventListeners } from '@/components/lib/GwUtils/utils_listeners'
import GwScrollContOverflowSurfaces from '@/components/lib/GwScrollCont/GwScrollContOverflowSurfaces.vue'
import GwBtn from '../GwBtn/GwBtn.vue'

export interface GwScrollContResizeProps {
  entries: ResizeObserverEntry[]
  height: number
  width: number
}

export interface GwScrollContProps {
  direction?: 'horizontal' | 'vertical' | 'omni'
  scrollbar?: 'none' | 'small' | 'medium' | 'large'
  hasOverflowSurfaces?: boolean
  overflowSurfaceSize?: 'small' | 'medium' | 'large'
  hasScrollArrows?: boolean
  hasEdgeAutoScroll?: boolean
  hasDragScroll?: boolean
  edgeAutoScrollAreaPx?: number
  autoScrollSpeed?: number
  hasResizeObserver?: boolean
  stretched?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<GwScrollContProps>(), {
  scrollbar: 'none',
  direction: 'horizontal',
  overflowSurfaceSize: 'medium',
  edgeAutoScrollAreaPx: 42,
  autoScrollSpeed: 2.5,
})

const emit = defineEmits([
  'scroll',
  'scroll:ignored',
  'scrolled:start',
  'scrolled:end',
  'resized',
  'overflow',
])

const content = ref()
const oTop = ref(0)
const oBottom = ref(0)
const oLeft = ref(0)
const oRight = ref(0)
let isDragScrolling = false

const overflow = computed(() => ({
  top: props.disabled ? 0 : oTop.value,
  right: props.disabled ? 0 : oRight.value,
  bottom: props.disabled ? 0 : oBottom.value,
  left: props.disabled ? 0 : oLeft.value,
  // ...(props.gwOverflowSurfacesProps || {}),
}))
const overflowMaskStyles = computed(() => {
  const { top, right, bottom, left } = overflow.value
  const maskHorizontal =
    left && right
      ? 'var(--mask-left-right)'
      : left
        ? 'var(--mask-left)'
        : right
          ? 'var(--mask-right)'
          : 'none'
  const maskVertical =
    top && bottom
      ? 'var(--mask-top-bottom)'
      : top
        ? 'var(--mask-top)'
        : bottom
          ? 'var(--mask-bottom)'
          : 'none'

  return {
    maskHorizontal: `mask-image: ${maskHorizontal}`,
    maskVertical: `mask-image: ${maskVertical}`,
  }
})
const checkScrolledStartEnd = () => {
  if (!content.value) return
  const isHorizontal = props.direction === 'horizontal'
  const scrolledToStart = isHorizontal ? oLeft.value === 0 : oTop.value === 0
  const scrolledToEnd = isHorizontal ? oRight.value === 0 : oBottom.value === 0

  if (scrolledToStart) {
    emit('scrolled:start')
  }
  if (scrolledToEnd) {
    emit('scrolled:end')
  }
}
const checkOverflow = () => {
  if (!content.value) return
  const cntv = content.value
  oTop.value = Math.max(0, cntv.scrollTop)
  oRight.value = Math.max(0, cntv.scrollWidth - cntv.clientWidth - cntv.scrollLeft - 1)
  oBottom.value = Math.max(0, cntv.scrollHeight - cntv.clientHeight - cntv.scrollTop - 1)
  oLeft.value = Math.max(0, cntv.scrollLeft)
  if (props.direction === 'horizontal') {
    oTop.value = 0
    oBottom.value = 0
  } else if (props.direction === 'vertical') {
    oLeft.value = 0
    oRight.value = 0
  }
  emit('overflow', {
    top: oTop.value,
    right: oRight.value,
    bottom: oBottom.value,
    left: oLeft.value,
  })
}

// Is scrolling utillity
// let timeoutIsScrolling: ReturnType<typeof setTimeout>
// const setIsScrolling = (val: boolean) => {
//   isScrolling.value = val
// }
// setIsScrolling(true)
// clearTimeout(timeoutIsScrolling)
// timeoutIsScrolling = setTimeout(() => {
//   setIsScrolling(false)
// }, 44)

const handleScroll = throttle((e: any) => {
  emit('scroll', e)
  checkOverflow()
  checkScrolledStartEnd()
}, 20)
const wheelScroll = (event: WheelEvent) => {
  if (props.direction === 'horizontal') {
    if (!content.value) return
    event.preventDefault()
    content.value.scrollLeft += event.deltaY
  }
}
const initDragScroll = (event: MouseEvent) => {
  if (!content.value) return
  const startScrollLeft = content.value.scrollLeft
  const startScrollTop = content.value.scrollTop

  const mousemove = (_event: MouseEvent) => {
    if (!props.hasDragScroll || !_event) return // this can be turned off during drag
    // event.preventDefault()
    if (props.direction === 'omni') {
      content.value.scrollLeft = startScrollLeft - (_event.clientX - event.clientX)
      content.value.scrollTop = startScrollTop - (_event.clientY - event.clientY)
      content.value.scrollLeft = startScrollLeft - (_event.clientX - event.clientX)
    } else if (props.direction === 'horizontal') {
      content.value.scrollLeft = startScrollLeft - (_event.clientX - event.clientX)
    } else {
      content.value.scrollTop = startScrollTop - (_event.clientY - event.clientY)
    }
  }
  const mouseup = () => {
    isDragScrolling = false
    removeEventListeners([
      [document, 'mousemove', mousemove],
      [document, 'mouseup', mouseup],
    ])
  }
  isDragScrolling = true
  addEventListeners([
    [document, 'mousemove', mousemove],
    [document, 'mouseup', mouseup],
  ])
}
const scrollToOffset = (offset: number, behavior: string = 'smooth') => {
  if (content.value) {
    if (props.direction === 'horizontal') {
      content.value.scroll({ left: offset })
      return
    }
    content.value.scroll({ top: offset, behavior })
  }
}

let timeoutAutoScroll: ReturnType<typeof setTimeout>
const scrollBy = (px: number, direction: 'left' | 'top') => {
  if (!content.value) return
  if (direction === 'left') {
    content.value.scrollLeft += px
  } else {
    content.value.scrollTop += px
  }
}

const clearAutoScrollInterval = () => {
  clearInterval(timeoutAutoScroll)
}

const setAutoScrollInterval = (backFront: number, direction: 'left' | 'top') => {
  clearAutoScrollInterval()
  timeoutAutoScroll = setInterval(() => {
    if (!props.hasEdgeAutoScroll || (props.hasDragScroll && isDragScrolling)) {
      clearAutoScrollInterval()
      return
    }
    scrollBy(backFront * props.autoScrollSpeed, direction)
  }, 1)
}
const handleMousemoveAutoScroll = (event: any) => {
  if (!content.value) return

  clearAutoScrollInterval()
  if (!props.hasEdgeAutoScroll) return

  const { top, bottom, left, right } = content.value.getBoundingClientRect()
  const { clientY, clientX } = event instanceof TouchEvent ? event.touches[0] : event
  const srcl = {
    toTop: clientY > top && clientY < top + props.edgeAutoScrollAreaPx,
    toBottom: clientY > bottom - props.edgeAutoScrollAreaPx && clientY < bottom,
    toLeft: clientX > left && clientX < left + props.edgeAutoScrollAreaPx,
    toRight: clientX > right - props.edgeAutoScrollAreaPx && clientX < right,
    clientY,
    clientX,
    top,
    bottom,
    right,
    left,
  }

  if (props.direction === 'horizontal') {
    srcl.toBottom = false
    srcl.toTop = false
  } else if (props.direction === 'vertical') {
    srcl.toLeft = false
    srcl.toRight = false
  }

  if (srcl.toTop || srcl.toBottom || srcl.toLeft || srcl.toRight) {
    isDragScrolling = false
    if (srcl.toTop || srcl.toBottom) {
      setAutoScrollInterval(srcl.toTop ? -1 : 1, 'top')
    }
    if (srcl.toLeft || srcl.toRight) {
      setAutoScrollInterval(srcl.toLeft ? -1 : 1, 'left')
    }
    return
  }

  clearAutoScrollInterval()
}
const initEdgeAutoScroll = () => {
  if (!props.hasEdgeAutoScroll) return
  if (!content.value) return
  addEventListeners([
    [document, 'mousemove', handleMousemoveAutoScroll],
    [document, 'touchmove', handleMousemoveAutoScroll],
  ])
}
const stopEdgeAutoScroll = () => {
  clearAutoScrollInterval()
  removeEventListeners([
    [document, 'mousemove', handleMousemoveAutoScroll],
    [document, 'touchmove', handleMousemoveAutoScroll],
  ])
}

onMounted(() => {
  nextTick(() => {
    checkOverflow()
  })
})

const scrollCont = ref()
if (props.hasResizeObserver) {
  const resizeObserver = new ResizeObserver(entries => {
    const res: GwScrollContResizeProps = {
      entries,
      width: scrollCont.value.clientWidth,
      height: scrollCont.value.clientHeight,
    }
    emit('resized', res)
    checkOverflow()
  })
  onMounted(() => {
    resizeObserver.observe(scrollCont.value)
  })
  onBeforeUnmount(() => {
    resizeObserver.disconnect()
  })
}

// can be acessed from parent
defineExpose({
  scrollToOffset,
  checkOverflow,
})

/* Somehin to keep in mind for later
hscrl_handleTouchend(e) {
      const scroller = this.$refs.scroller?.$el || this.$refs.scroller
      if (!scroller) return
      // add info on event for parent handling swipe events
      const timestamp = Date.now()
      const ist_top = scroller.scrollTop <= 0
      const ist_bottom = scroller.scrollTop >= scroller.scrollHeight - scroller.clientHeight
      const ist_left = scroller.scrollLeft <= 0
      const ist_right = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth

      const ist_top_time =
        this.hscrl_surface?.ist_to === ist_top ? this.hscrl_surface.ist_top_time || 0 : timestamp
      const ist_bottom_time =
        this.hscrl_surface?.ist_bottom === ist_bottom
          ? this.hscrl_surface.ist_bottom_time || 0
          : timestamp
      const ist_left_time =
        this.hscrl_surface?.ist_left === ist_left
          ? this.hscrl_surface.ist_left_time || 0
          : timestamp
      const ist_right_time =
        this.hscrl_surface?.ist_right === ist_right
          ? this.hscrl_surface.ist_right_time || 0
          : timestamp

      const surface = {
        timestamp,
        ist_first: !this.hscrl_surface,
        ist_top,
        ist_bottom,
        ist_left,
        ist_right,
        ist_top_time,
        ist_bottom_time,
        ist_left_time,
        ist_right_time,
      }

      this.hscrl_surface = surface
      e.gw_touchends = [...(e.gw_touchends || []), surface]
    } */
</script>

<template>
  <div
    ref="scrollCont"
    class="gwsclcnt"
    :class="[`gwsclcnt_direction_${direction}`, disabled && 'gwsclcnt_disabled']"
    @wheel.stop="e => wheelScroll(e)"
    @mousedown="e => initDragScroll(e)"
    @mouseenter="initEdgeAutoScroll()"
    @touchstart="initEdgeAutoScroll()"
    @mouseleave="stopEdgeAutoScroll()"
    @touchend="stopEdgeAutoScroll()"
  >
    <slot name="content_before"></slot>
    <div
      class="gwsclcnt_content_wrapper"
      :class="[
        stretched ? 'gwsclcnt_content_wrapper_stretched' : '',
        hasOverflowSurfaces && `gwsclcnt_mask gwsclcnt_mask_size_${overflowSurfaceSize}`,
      ]"
      :style="hasOverflowSurfaces && overflowMaskStyles.maskVertical"
    >
      <div
        ref="content"
        class="gwsclcnt_content"
        :class="`scrolltrack scrolltrack_${scrollbar}`"
        :style="hasOverflowSurfaces && overflowMaskStyles.maskHorizontal"
        @scroll="handleScroll($event)"
      >
        <slot
          name="default"
          :overflow
        ></slot>
      </div>
      <GwScrollContOverflowSurfaces
        v-if="hasOverflowSurfaces"
        :top="oTop"
        :bottom="oBottom"
        :left="oLeft"
        :right="oRight"
      >
        <template
          v-for="arrow in hasScrollArrows
            ? [
                { slot: 'top', scrollPx: -200, direction: 'top', icon: 'mdi-chevron-top' },
                {
                  slot: 'right',
                  scrollPx: 200,
                  direction: 'left',
                  icon: 'mdi-chevron-right',
                },
                {
                  slot: 'bottom',
                  scrollPx: 200,
                  direction: 'top',
                  icon: 'mdi-chevron-bottom',
                },
                { slot: 'left', scrollPx: -200, direction: 'left', icon: 'mdi-chevron-left' },
              ]
            : []"
          :key="arrow.slot"
          #[arrow.slot]
        >
          <GwBtn
            variant="btn_text_white"
            :icon="arrow.icon"
            @click="scrollBy(arrow.scrollPx, arrow.direction as 'left' | 'top')"
          ></GwBtn>
        </template>
      </GwScrollContOverflowSurfaces>
    </div>
    <slot name="content_after"></slot>
  </div>
</template>

<style lang="scss" scoped>
.gwsclcnt {
  position: relative;
  display: flex;
  min-height: 0;
  min-width: 0;
  max-height: 100%;
  max-width: 100%;
  overflow: hidden;

  --gwsclcnt-overflow-y: hidden;
  --gwsclcnt-overflow-x: hidden;
  --gwsclcnt-overlay-bg: var(--clr-black-2);
  --gwsclcnt-content-direction: row;

  &_disabled {
    pointer-events: none;
  }
  &_content_wrapper {
    position: relative;
    display: flex;
    width: 100%;
    min-height: 0;
    min-width: 0;
    max-width: 100%;
    max-height: 100%;
    &_stretched {
      flex: 1;
    }
  }
  &_content {
    flex: 1;
    display: flex;
    overflow-y: var(--gwsclcnt-overflow-y);
    overflow-x: var(--gwsclcnt-overflow-x);
    flex-direction: var(--gwsclcnt-content-direction);
    min-height: 0;
    min-width: 0;
    max-height: 100%;
    max-width: 100%;
  }
  &_direction {
    &_horizontal {
      --gwsclcnt-overflow-y: hidden;
      --gwsclcnt-overflow-x: auto;
      --gwsclcnt-content-direction: row;
    }
    &_vertical {
      --gwsclcnt-overflow-y: auto;
      --gwsclcnt-overflow-x: hidden;
      --gwsclcnt-content-direction: column;
    }
    &_omni {
      --gwsclcnt-overflow-y: auto;
      --gwsclcnt-overflow-x: auto;
      --gwsclcnt-content-direction: column;
    }
  }
  &_disabled {
    --gwsclcnt-overflow-y: hidden;
    --gwsclcnt-overflow-x: hidden;
  }
  &_mask {
    --gwsclcnt-mask-size: 2.5rem;

    &_size {
      &_small {
        --gwsclcnt-mask-size: 2.5rem;
      }
      &_medium {
        --gwsclcnt-mask-size: 3.5rem;
      }
      &_large {
        --gwsclcnt-mask-size: 4.5rem;
      }
    }

    // https://ishadeed.com/article/css-masking/ - reference article
    // I dont know how to do combinations - etc. --mask-left-right-top-bottom
    // theres this post about it https://stackoverflow.com/questions/73072967/css-mask-image-gradient-on-left-side-and-bottom
    // can be done but it will need some time to set it up correctly using css variables
    // or maybe we could do it using a wrapper div and then using what we already have here

    --mask-one-side: transparent 0, transparent 0px, #000 var(--gwsclcnt-mask-size), #000 100%;
    --mask-both-sides: transparent 0, transparent 0px, #000 var(--gwsclcnt-mask-size), #000 50%,
      transparent 50%, transparent 100%;

    --mask-left: linear-gradient(to right, var(--mask-one-side));
    --mask-right: linear-gradient(to left, var(--mask-one-side));
    --mask-top: linear-gradient(to bottom, var(--mask-one-side));
    --mask-bottom: linear-gradient(to top, var(--mask-one-side));

    --mask-left-right: linear-gradient(to right, var(--mask-both-sides)),
      linear-gradient(to left, var(--mask-both-sides));
    --mask-top-bottom: linear-gradient(to bottom, var(--mask-both-sides)),
      linear-gradient(to top, var(--mask-both-sides));

    // --dev purposes
    // background: orange;
    // height: 444px;
    // mask-image: var(--mask-left);
    // mask-image: var(--mask-right);
    // mask-image: var(--mask-left-right);
    // mask-image: var(--mask-top);
    // mask-image: var(--mask-bottom);
    // mask-image: var(--mask-top-bottom);
  }
}

.scrolltrack {
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  &::-webkit-scrollbar {
    background-color: transparent;
  }

  &_none {
    scrollbar-width: none;
    &::-webkit-scrollbar-track {
      width: 0px;
      height: 0px;
    }
    &::-webkit-scrollbar {
      width: 0px;
      height: 0px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: transparent;
      width: 0px;
      height: 0px;
    }
  }
  &_small {
    scrollbar-width: thin;
    scrollbar-color: var(--clr-black-3) transparent;
    &::-webkit-scrollbar-thumb {
      background-color: var(--clr-black-3);
    }
    &::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }
  }
  &_medium {
    scrollbar-width: thin;
    scrollbar-color: var(--clr-black-3) transparent;
    &::-webkit-scrollbar-thumb {
      background-color: var(--clr-black-3);
    }
    &::-webkit-scrollbar {
      width: 0.375rem;
      height: 0.375rem;
    }
  }
  &_large {
    scrollbar-width: auto;
    scrollbar-color: var(--clr-black-3) transparent;
    &::-webkit-scrollbar-thumb {
      background-color: var(--clr-black-3);
      min-height: 4rem;
    }

    &::-webkit-scrollbar {
      width: 0.75rem;
      height: 0.75rem;
    }
  }
}
</style>
